const SESSION_KEY = 'salaryCalculator';

function saveSession(arbeitsweltKey, erfahrungKey, stunden) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ arbeitsweltKey, erfahrungKey, stunden }));
}

function loadSession() {
    try {
        return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || {};
    } catch {
        return {};
    }
}

function formatSalary(value) {
    return value.toLocaleString('de-DE');
}

function updateSliderFill(slider) {
    const min = parseFloat(slider.min) || 0;
    const max = parseFloat(slider.max) || 100;
    const percent = ((parseFloat(slider.value) - min) / (max - min)) * 100;
    slider.style.setProperty('--fill', percent + '%');
}

// Labels der Slider je Instanz eindeutig halten. Das Template liefert stabile
// IDs (salaryCalcLevelLabel / salaryCalcHoursLabel), damit die Beschriftung auch
// ohne JS steht; taucht der Blueprint mehrfach auf einer Seite auf, wuerden die
// IDs kollidieren – deshalb ab der zweiten Instanz durchnummerieren.
function scopeLabelIds(calculator, index) {
    if (index === 0) return;
    calculator.querySelectorAll('[id^="salaryCalc"][id$="Label"]').forEach(label => {
        const scopedId = `${label.id}-${index}`;
        calculator
            .querySelectorAll(`[aria-labelledby="${label.id}"]`)
            .forEach(target => target.setAttribute('aria-labelledby', scopedId));
        label.id = scopedId;
    });
}

// Der Erfahrungs-Slider transportiert nur den Index 0..n. Ein Screenreader liest
// damit eine nackte Zahl vor – aria-valuetext liefert die Stufenbezeichnung nach.
function updateErfahrungValueText(slider, erfahrungLabels) {
    const idx = parseInt(slider.value, 10);
    const activeSpan = erfahrungLabels[idx];
    const key = activeSpan?.dataset.key || null;
    let text = activeSpan?.textContent.trim() || '';
    if (!text && key?.endsWith('_plus')) {
        const baseSpan = [...erfahrungLabels].find(s => s.dataset.key === key.replace('_plus', ''));
        const baseText = baseSpan?.textContent.trim() || '';
        text = baseText ? `${baseText} Plus` : '';
    }
    if (text) slider.setAttribute('aria-valuetext', text);
    else slider.removeAttribute('aria-valuetext');
}

function updateResult(calculator, salaryData) {
    const activeTile = calculator.querySelector('.salaryCalculator__tile.is-active');
    const arbeitsweltKey = activeTile?.dataset.key || null;
    const arbeitsweltLabel = activeTile?.textContent.trim() || '';

    const erfahrungSlider = calculator.querySelector('.js-salary-erfahrung');
    const erfahrungLabels = calculator.querySelectorAll('.js-salary-erfahrung-labels span');
    const erfahrungIdx = erfahrungSlider ? parseInt(erfahrungSlider.value, 10) : 0;
    const activeErfahrungSpan = erfahrungLabels[erfahrungIdx];
    const erfahrungKey = activeErfahrungSpan?.dataset.key || null;
    let erfahrungLabel = activeErfahrungSpan?.textContent.trim() || '';
    if (!erfahrungLabel && erfahrungKey?.endsWith('_plus')) {
        const baseSpan = [...erfahrungLabels].find(s => s.dataset.key === erfahrungKey.replace('_plus', ''));
        erfahrungLabel = baseSpan?.textContent.trim() || '';
    }

    const arbeitsweltLabelEl = calculator.querySelector('.js-salary-arbeitswelt-label');
    const erfahrungLabelEl = calculator.querySelector('.js-salary-erfahrung-label');
    if (arbeitsweltLabelEl) arbeitsweltLabelEl.textContent = arbeitsweltLabel;
    if (erfahrungLabelEl) erfahrungLabelEl.textContent = erfahrungLabel;

    const rangeEl = calculator.querySelector('.js-salary-range');
    if (!rangeEl) return;

    const stundenSlider = calculator.querySelector('.js-salary-stunden');
    const stunden = stundenSlider ? parseFloat(stundenSlider.value) : 40;
    const faktor = stunden / 40;

    const arbeitsweltData = arbeitsweltKey ? salaryData[arbeitsweltKey] : null;
    const salary = erfahrungKey && arbeitsweltData ? arbeitsweltData[erfahrungKey] : null;

    const rangeSkeleton = rangeEl.querySelector('.salaryCalculator__rangeSkeleton');
    if (rangeSkeleton) rangeSkeleton.remove();

    if (salary) {
        const min = Math.round(salary.min * faktor);
        const max = Math.round(salary.max * faktor);
        rangeEl.textContent = min === max
            ? `${formatSalary(min)} €`
            : `${formatSalary(min)} - ${formatSalary(max)} €`;
    } else {
        rangeEl.textContent = '–';
    }

    const effectiveHintKey = erfahrungKey === 'gap' ? 'trainee' : erfahrungKey;
    const levelHints = calculator.querySelectorAll('.js-salary-level-hint');
    levelHints.forEach(hint => {
        hint.style.display = hint.dataset.key === effectiveHintKey ? '' : 'none';
    });
}

function bindInteractions(calculator, salaryData) {
    const session = loadSession();

    // Sektionen bei nur 1 Option ausblenden
    const arbeitsweltenSection = calculator.querySelector('.js-salary-section-arbeitswelten');
    const tiles = arbeitsweltenSection
        ? arbeitsweltenSection.querySelectorAll('.salaryCalculator__tile')
        : [];
    if (arbeitsweltenSection && tiles.length <= 1) {
        arbeitsweltenSection.style.display = 'none';
    }

    const erfahrungSection = calculator.querySelector('.js-salary-section-erfahrung');
    const erfahrungLabels = calculator.querySelectorAll('.js-salary-erfahrung-labels span');
    // Nur echte, waehlbare Stufen zaehlen. Der 'gap'-Span nach Trainee ist ein
    // reiner Spacer und wird im Slider uebersprungen – er zaehlt nicht. Die leeren
    // '*_plus'-Spans SIND dagegen waehlbare Stufen (eigene Gehaltsdaten), auch wenn
    // sie keinen Text haben, und muessen mitgezaehlt werden. Sonst wird bei z.B. nur
    // "Junior" (Junior + Junior Plus = 2 Stufen) der Bereich faelschlich ausgeblendet.
    // Erst bei hoechstens einer echten Stufe (z.B. nur "trainee") ausblenden (#224317).
    const realErfahrungLevels = [...erfahrungLabels].filter(s => s.dataset.key !== 'gap');
    if (erfahrungSection && realErfahrungLevels.length <= 1) {
        erfahrungSection.style.display = 'none';
    }

    // Arbeitswelt: Session oder erstes Tile
    if (tiles.length > 0) {
        tiles.forEach(t => t.classList.remove('is-active'));
        const savedTile = session.arbeitsweltKey
            ? [...tiles].find(t => t.dataset.key === session.arbeitsweltKey)
            : null;
        (savedTile || tiles[0]).classList.add('is-active');
    }

    tiles.forEach(tile => {
        tile.addEventListener('click', () => {
            tiles.forEach(t => t.classList.remove('is-active'));
            tile.classList.add('is-active');
            const erfahrungSlider = calculator.querySelector('.js-salary-erfahrung');
            const erfahrungLabels = calculator.querySelectorAll('.js-salary-erfahrung-labels span');
            const activeErfahrung = erfahrungLabels[parseInt(erfahrungSlider?.value, 10)];
            const stundenSlider = calculator.querySelector('.js-salary-stunden');
            saveSession(tile.dataset.key, activeErfahrung?.dataset.key, stundenSlider?.value);
            updateResult(calculator, salaryData);
        });
    });

    // Erfahrung slider: Session, data-default-key oder erstes Label
    const erfahrungSlider = calculator.querySelector('.js-salary-erfahrung');
    if (erfahrungSlider) {
        erfahrungSlider.max = Math.max(0, erfahrungLabels.length - 1);

        let defaultIdx = 0;
        if (session.erfahrungKey) {
            const found = [...erfahrungLabels].findIndex(s => s.dataset.key === session.erfahrungKey);
            defaultIdx = found >= 0 ? found : 0;
        } else {
            const defaultKey = erfahrungSlider.dataset.defaultKey;
            const found = defaultKey
                ? [...erfahrungLabels].findIndex(s => s.dataset.key === defaultKey)
                : -1;
            defaultIdx = found >= 0 ? found : 0;
        }
        erfahrungSlider.value = defaultIdx;
        updateErfahrungValueText(erfahrungSlider, erfahrungLabels);

        erfahrungSlider.addEventListener('input', () => {
            const idx = parseInt(erfahrungSlider.value, 10);
            if (erfahrungLabels[idx]?.dataset.key === 'gap') {
                const prev = parseInt(erfahrungSlider.dataset.prevValue ?? idx, 10);
                const max = parseInt(erfahrungSlider.max, 10) || 0;
                // Gap-Position ueberspringen. Am Rand (kein gueltiges Label dahinter)
                // in die Gegenrichtung ausweichen und in [0, max] clampen, damit der
                // Slider nicht auf dem leeren Gap haengenbleibt und "–" zeigt (#224317).
                let target = idx > prev ? idx + 1 : idx - 1;
                if (target > max || target < 0) {
                    target = idx > prev ? idx - 1 : idx + 1;
                }
                erfahrungSlider.value = Math.min(Math.max(target, 0), max);
            }
            erfahrungSlider.dataset.prevValue = erfahrungSlider.value;
            updateSliderFill(erfahrungSlider);
            updateErfahrungValueText(erfahrungSlider, erfahrungLabels);
            const stundenSlider = calculator.querySelector('.js-salary-stunden');
            const activeTile = calculator.querySelector('.salaryCalculator__tile.is-active');
            const activeErfahrung = erfahrungLabels[parseInt(erfahrungSlider.value, 10)];
            saveSession(activeTile?.dataset.key, activeErfahrung?.dataset.key, stundenSlider?.value);
            updateResult(calculator, salaryData);
        });
        updateSliderFill(erfahrungSlider);
    }

    // Stunden slider: Session oder Default
    const stundenSlider = calculator.querySelector('.js-salary-stunden');
    const stundenValue = calculator.querySelector('.js-salary-stundenValue');
    if (stundenSlider) {
        if (session.stunden !== undefined) {
            stundenSlider.value = session.stunden;
            if (stundenValue) stundenValue.textContent = session.stunden + ' h';
        }
        stundenSlider.addEventListener('input', () => {
            if (stundenValue) stundenValue.textContent = stundenSlider.value + ' h';
            updateSliderFill(stundenSlider);
            const activeTile = calculator.querySelector('.salaryCalculator__tile.is-active');
            const activeErfahrungSpan = erfahrungLabels[parseInt(erfahrungSlider?.value, 10)];
            saveSession(activeTile?.dataset.key, activeErfahrungSpan?.dataset.key, stundenSlider.value);
            updateResult(calculator, salaryData);
        });
        updateSliderFill(stundenSlider);
    }

    updateResult(calculator, salaryData);
}

export function initSalaryCalculator() {
    const calculators = document.querySelectorAll('.js-salary-calculator');
    if (!calculators.length) return;

    calculators.forEach((calculator, index) => {
        // Vor dem Fetch, damit die Beschriftung auch dann steht, wenn das JSON
        // nicht laedt und bindInteractions nie laeuft.
        scopeLabelIds(calculator, index);

        const src = calculator.dataset.salarySrc;
        if (!src) return;

        fetch(src)
            .then(res => res.json())
            .then(salaryData => bindInteractions(calculator, salaryData))
            .catch(err => console.warn('salaryCalculator: JSON konnte nicht geladen werden', err));
    });
}
